import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Sparkles, Rocket, Handshake, Landmark, ArrowRight } from "lucide-react";
import WaitlistForm from "@/components/WaitlistForm";

const CATS = [
    {
        icon: Rocket,
        title: "Incubators",
        desc: "Find programs aligned with your sector, stage and ambition.",
    },
    {
        icon: Sparkles,
        title: "Grants",
        desc: "Discover non-dilutive funding opportunities relevant to your work.",
    },
    {
        icon: Landmark,
        title: "Accelerators",
        desc: "Explore programs that can provide capital, mentorship and market access.",
    },
    {
        icon: Handshake,
        title: "Ecosystem Partners",
        desc: "Connect with organizations, experts and institutions that can help you move forward.",
    },
];

export default function Ecosystem() {
    return (
        <main data-testid="ecosystem-page" className="text-white pt-32 pb-24">
            <SEO
                title="Ecosystem | Purposeful, Inc."
                description="Purposeful is building relationships with incubators, accelerators, grant programs and ecosystem partners so founders can discover opportunities and warm introductions where there's a genuine fit."
                path="/ecosystem"
                keywords="startup ecosystem, incubators, accelerators, grants, ecosystem partners, founder introductions, impact founder support"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "@id": "https://purposeful.inc/ecosystem#page",
                    "url": "https://purposeful.inc/ecosystem",
                    "name": "The Purposeful Ecosystem",
                    "description": "Incubators, accelerators, grant programs and ecosystem partners for founders building businesses that matter.",
                    "isPartOf": { "@id": "https://purposeful.inc/#website" },
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Ecosystem", path: "/ecosystem" }]}
            />

            <section className="container-x mx-auto max-w-7xl">
                <div className="overline mb-4">The Purposeful Ecosystem</div>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-4xl">
                    Find the right doors.<br />
                    <span className="text-[#D4AF37]">Build the right relationships.</span>
                </h1>
                <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-3xl">
                    Purposeful is building a network of incubators, accelerators,
                    grant programs and ecosystem partners that can help founders
                    move forward. Founders in the network can discover relevant
                    opportunities and make warm introductions where there's a
                    genuine fit.
                </p>
            </section>

            <section className="container-x mx-auto max-w-7xl mt-20">
                <div className="grid md:grid-cols-2 gap-6">
                    {CATS.map((c, i) => (
                        <div key={i} className="card-dark p-8" data-testid={`ecosystem-cat-${i}`}>
                            <c.icon size={22} className="text-[#D4AF37]" />
                            <h2 className="font-display text-2xl mt-5">{c.title}</h2>
                            <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{c.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="container-x mx-auto max-w-5xl mt-24" data-testid="ecosystem-promise">
                <div className="rounded-2xl border border-white/10 p-10 md:p-14 relative overflow-hidden">
                    <div className="absolute inset-0 fingerprint-bg" />
                    <div className="relative">
                        <div className="overline mb-4">The promise</div>
                        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">
                            We help founders find the right doors, and where appropriate, open them.
                        </h2>
                        <p className="mt-6 text-zinc-400 leading-relaxed max-w-2xl">
                            Purposeful doesn't guarantee grants, funding or
                            admission into programs. We identify relevant
                            opportunities, make introductions where there's a
                            genuine fit, and stay close as you navigate them.
                        </p>
                    </div>
                </div>
            </section>

            <section className="container-x mx-auto max-w-3xl mt-24 text-center" data-testid="ecosystem-cta">
                <div className="overline mb-4">Founder access</div>
                <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                    Looking for the<br />
                    <span className="text-[#D4AF37]">right introduction?</span>
                </h2>
                <p className="mt-6 text-zinc-400 text-lg">
                    Tell us what you're building and where you need help. If
                    there's a genuine fit within the Purposeful ecosystem,
                    we'll explore the right introduction.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Link to="/apply" data-testid="ecosystem-apply-cta" className="btn-gold">
                        Apply for Ecosystem Access <ArrowRight size={16} />
                    </Link>
                    <Link to="/contact?topic=Ecosystem%20Partner" data-testid="ecosystem-partner-cta" className="btn-ghost-gold">
                        Become an Ecosystem Partner <ArrowRight size={14} />
                    </Link>
                </div>
                <p className="mt-4 text-xs text-zinc-500 max-w-lg mx-auto">
                    Purposeful does not guarantee funding, admission or
                    introductions. Applications are reviewed based on
                    relevance and fit within the ecosystem.
                </p>
            </section>
        </main>
    );
}
